import { Component, DoCheck, EventEmitter, inject, Input, OnChanges, OnInit, Output, Pipe, PipeTransform, SimpleChanges, ViewChild } from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DisplayTranformation, FieldSelectedValue, FormFieldConfig,} from '../../../model/form.model';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CamelCasePipe } from '../../../utils/camel-case.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { FormService } from '../../../services/form.service';
import { MatSelect,MatSelectModule } from '@angular/material/select';
import { MatOptionSelectionChange } from '@angular/material/core';

@Pipe({
  name: 'visibleOptions',
  pure: true,
  standalone:true
})
export class VisibleOptionsPipe implements PipeTransform {
  transform(options: any[], field: any, autoCompleteInput: string): any[] {
    return options?.filter(opt => {
      if (field?.isAutoComplete) {
        return opt?.fieldLabel?.toLowerCase().includes(autoCompleteInput?.toLowerCase());
      }
      return true;
    });
  }
}

@Component({
  selector: 'app-mat-dropdown-field',
  standalone: true,
  imports: [FormsModule,VisibleOptionsPipe,MatSelectModule,CommonModule,MatFormFieldModule,MatInputModule,CamelCasePipe,MatProgressSpinnerModule,ReactiveFormsModule],
  templateUrl: './mat-dropdown-field.component.html',
  styleUrl: './mat-dropdown-field.component.scss',
})
export class MatDropdownFieldComponent implements OnInit,OnChanges,DoCheck{
  readonly DisplayTransformation: typeof DisplayTranformation =DisplayTranformation;
  readonly selectAllSentinel = '__select_all__';
  @Input() field!: FormFieldConfig | undefined;
  @Input() form!: FormGroup;
  @Output() selectionChanged = new EventEmitter<FieldSelectedValue>();
  formService = inject(FormService);
  autoCompleteInput = '';
  autoCompleteFilter = '';
  formFieldName:any = ''
  private lastSyncedValueKey = '';
  private lastOptionsKey = '';
@ViewChild('dropdownRef') matSelect!: MatSelect;
 ngOnInit(): void {
  this.formFieldName = this.field?.isAutoComplete ? `${this.field?.['fieldName']}_autoComplete` : this.field?.['fieldName'];
  const fieldName = this.field?.['fieldName'];
  const control = fieldName ? this.form.get(fieldName) : null;
  
  if (control) {
    // Sync form control with field.selectedValue if it exists
    if (this.field?.selectedValue && !control.value) {
      control.setValue(this.field.selectedValue, { emitEvent: false });
      
      // For autocomplete fields, also set the display label
      if (this.field?.isAutoComplete && this.field?.options?.length) {
        const selectedOption = this.field.options.find((opt: any) => opt?.fieldValue === this.field?.selectedValue);
        if (selectedOption) {
          this.autoCompleteInput = selectedOption?.fieldLabel || '';
          this.autoCompleteFilter = '';
        }
      }
    }
    
    this.form.valueChanges.subscribe(() => {
      this.dynamicFieldDisabled()
    });
    this.dynamicFieldDisabled();
  }
}

  ngDoCheck(): void {
    this.dynamicFieldDisabled();
    this.syncFieldValueToControl();
  }
  
  ngOnChanges(changes: SimpleChanges): void { 
    const field = changes['field']?.currentValue?.selectedValue;
    const prevField = changes['field']?.previousValue?.selectedValue;

    if (field !== prevField && [null, undefined, ''].includes(field)) {
      this.autoCompleteInput = '';
    }

    // Sync form control when options are loaded (for async-loaded dropdowns like Dealer)
    if (changes['field']) {
      const fieldName = this.field?.['fieldName'];
      const control = fieldName ? this.form.get(fieldName) : null;
      
      // If options just finished loading and we have a selectedValue, sync it
      const currentOptions = changes['field']?.currentValue?.options;
      const prevOptions = changes['field']?.previousValue?.options;
      
      if (control && this.field?.selectedValue && currentOptions?.length && (!prevOptions?.length)) {
        // Options just loaded, sync the selected value to form control
        control.setValue(this.field.selectedValue, { emitEvent: false });
        
        // For autocomplete fields, also set the display label
        if (this.field?.isAutoComplete && currentOptions?.length) {
          const selectedOption = currentOptions.find((opt: any) => opt?.fieldValue === this.field?.selectedValue);
          if (selectedOption) {
            this.autoCompleteInput = selectedOption?.fieldLabel || '';
            this.autoCompleteFilter = '';
          }
        }
      } else if (control && field !== prevField && this.field?.selectedValue) {
        // selectedValue property changed, sync to form control
        control.setValue(this.field.selectedValue, { emitEvent: false });
        
        // For autocomplete fields, also set the display label
        if (this.field?.isAutoComplete && this.field?.options?.length) {
          const selectedOption = this.field.options.find((opt: any) => opt?.fieldValue === this.field?.selectedValue);
          if (selectedOption) {
            this.autoCompleteInput = selectedOption?.fieldLabel || '';
            this.autoCompleteFilter = '';
          }
        }
      }
    }
  }

  private syncFieldValueToControl(): void {
    const fieldName = this.field?.['fieldName'];
    const control = fieldName ? this.form?.get(fieldName) : null;
    if (!control || !this.field) {
      return;
    }

    const valueKey = this.getComparableKey(this.field.selectedValue);
    const optionsKey = this.getComparableKey((this.field.options ?? []).map((opt: any) => opt?.fieldValue));
    const shouldSyncValue = valueKey !== this.lastSyncedValueKey;
    const shouldRefreshLabel = this.field.isAutoComplete && optionsKey !== this.lastOptionsKey;

    if (!shouldSyncValue && !shouldRefreshLabel) {
      return;
    }

    const normalizedValue = this.field.multiple
      ? this.normalizeMultipleValue(this.field.selectedValue)
      : (this.field.selectedValue ?? '');

    if (shouldSyncValue && !this.areValuesEqual(control.value, normalizedValue)) {
      control.setValue(normalizedValue, { emitEvent: false });
    }

    if (this.field.isAutoComplete) {
      this.syncAutoCompleteLabel();
    }

    this.lastSyncedValueKey = valueKey;
    this.lastOptionsKey = optionsKey;
  }

  private syncAutoCompleteLabel(): void {
    if (!this.field?.options?.length) {
      return;
    }

    const selectedOption = this.field.options.find((opt: any) => opt?.fieldValue === this.field?.selectedValue);
    this.autoCompleteInput = selectedOption?.fieldLabel || '';
    if (selectedOption) {
      this.autoCompleteFilter = '';
    }
  }

  private getComparableKey(value: any): string {
    if (Array.isArray(value)) {
      return JSON.stringify(value);
    }

    if (value && typeof value === 'object') {
      return JSON.stringify(value);
    }

    return String(value ?? '');
  }

  private areValuesEqual(left: any, right: any): boolean {
    return this.getComparableKey(left) === this.getComparableKey(right);
  }

  onOpenClose(event: any) {
    if (event) {
      this.syncSelectAllOptionState();
    }

    if (this.field?.isAutoComplete && !event && this.field?.options) {
      const selectedOption = this.field.options.find(option => option?.fieldLabel?.toLowerCase() === this.autoCompleteInput?.toLowerCase());
      const fieldName = this.field?.['fieldName'];
      const control = fieldName ? this.form.get(fieldName) : null;
      
      if (selectedOption) {
        this.autoCompleteInput = selectedOption['fieldLabel'];
        this.field.selectedValue = selectedOption['fieldValue'];
        this.autoCompleteFilter = '';
        if (control) {
          control.setValue(selectedOption['fieldValue'], { emitEvent: false });
        }
      } else {
        this.autoCompleteInput = '';
        this.field.selectedValue = '';
        this.autoCompleteFilter = '';
        if (control) {
          control.setValue('', { emitEvent: false });
        }
      }
    }
  }
dynamicFieldDisabled(): void {
  const fieldName = this.field?.['fieldName'];
  const control = fieldName && this.form.get(fieldName);
  const shouldDisable = this.field?.['disabled'] || this.field?.['isLoading'];
  if (control) {
    shouldDisable ? control.disable({ emitEvent: false }) : control.enable({ emitEvent: false });
  }
}

  private handleSelectAll(): void {
    const allSelected = this.field?.selectedValue?.length === this.field?.options?.length;
    if(this.field){
    if (allSelected) {
      this.field.selectedValue = [];
    } else {
      const selectedAll = this.field.options?.map((opt: any) => opt['fieldValue']) ?? [];
      this.field.selectedValue = selectedAll;
    }
  }
  }

  private handleSingleSelection(Value?: any): void {
    if(this.field){
    if (this.field.options) {
      const selectedOption = this.field.options.find((opt: any) => opt['fieldValue'] === Value);
      if (selectedOption) {
        this.autoCompleteInput = selectedOption['fieldLabel'];
        this.autoCompleteFilter = '';
      } else {
        this.autoCompleteInput = '';
        this.autoCompleteFilter = '';
      }
    }
    if (this.field.multiple) {
      this.field.selectedValue = this.normalizeMultipleValue(Value);
    } else {
      this.field.selectedValue = Value;
    }
  }
  }

  handleSelection(FieldName: string, Value?: any, selectAll = false): void {
    if (this.field) {
      if (selectAll) {
        this.handleSelectAll();
      } else {
        if (Array.isArray(Value)) {
          Value = Value.filter((v: any) => v !== this.selectAllSentinel);
        }
        this.handleSingleSelection(Value);
      }

      if (this.field.multiple) {
        this.field.selectedValue = this.normalizeMultipleValue(this.field.selectedValue);
      }

      // Update form control with new value using the correct form field name
      const controlName = this.formFieldName || FieldName;
      const control = this.form.get(controlName);
      if (control) {
        const normalizedValue = this.field.multiple
          ? this.normalizeMultipleValue(this.field.selectedValue)
          : this.field.selectedValue;
        control.setValue(normalizedValue, { emitEvent: false });
      }
      this.syncSelectAllOptionState();
      this.selectionChanged.emit({ FieldName: FieldName, Value: this.field.selectedValue });
    }
  }

  onSelectAllOptionChange(event: MatOptionSelectionChange): void {
    if (!event.isUserInput || !this.field) {
      return;
    }

    this.handleSelection(this.field['fieldName'], null, true);
  }

openDropdown(): void {
    if (this.field?.disabled || this.field?.isLoading) {
      return;
    }
    this.autoCompleteFilter = '';

    if (this.matSelect) {
      this.matSelect.open();
    } else {
      console.info('matSelect not available');
    }
  
}

onAutoCompleteInputChange(): void {
  if (this.field?.disabled || this.field?.isLoading) {
    return;
  }
  this.autoCompleteFilter = this.autoCompleteInput ?? '';
  if (this.matSelect && !this.matSelect.panelOpen) {
    this.matSelect.open();
  }
}

isAllSelected() {
  if (this.field) {
    return this.field.selectedValue?.length === this.field.options?.length;
  }
  return false;
  }

  private syncSelectAllOptionState(): void {
    if (!this.field?.multiple || !this.matSelect?.options) {
      return;
    }

    const selectAllOption = this.matSelect.options.find((opt: any) => opt?.value === this.selectAllSentinel);
    if (!selectAllOption) {
      return;
    }

    if (this.isAllSelected()) {
      selectAllOption.select(false);
    } else {
      selectAllOption.deselect(false);
    }
  }

  trackByOption(index: number, opt: any): any {
    return opt?.fieldValue ?? index;
  }

  private normalizeMultipleValue(value: any): any[] {
    if (Array.isArray(value)) {
      return value.filter((item) => item !== null && item !== undefined && item !== '');
    }
    return value !== null && value !== undefined && value !== '' ? [value] : [];
  }
}
